import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { CircleOff } from "lucide-react-native";
import Text from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { DiseaseDetail } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { getCategoryFromRoute } from "../../buildDiseaseHistoryNavigationStack";
import { useFlowProgress } from "../../useFlowProgress";
import {
  DISEASE_CATEGORY_OPTIONS,
  DISEASES_BY_CATEGORY,
  type DiseaseCategoryType,
  type DiseaseType,
} from "../options";
import type { DiseaseHistoryFlowRoute } from "../../buildDiseaseHistoryNavigationStack";
import { useMedicalHistoryTheme } from "../../../../theme/MedicalHistoryTheme";

export function DiseaseSelectionScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const theme = useMedicalHistoryTheme();
  const route = useRoute();
  const { data: patient } = usePatient();
  const patientSex = patient?.gender as "AFAB" | "AMAB" | null | undefined;

  const routeName = route.name as DiseaseHistoryFlowRoute;
  const category = getCategoryFromRoute(routeName) as DiseaseCategoryType;
  const progress = useFlowProgress(routeName);

  const categoryOption = useMemo(
    () => DISEASE_CATEGORY_OPTIONS.find((c) => c.id === category),
    [category],
  );

  const diseases = useMemo(
    () =>
      (DISEASES_BY_CATEGORY[category] ?? []).filter(
        (o) => o.sex === "both" || !patientSex || o.sex === patientSex,
      ),
    [category, patientSex],
  );

  // Selected diseases for this category
  const selectedDiseases = useMemo(
    () =>
      new Set(
        state.diseases
          .filter((d) => d.category === category)
          .map((d) => d.disease),
      ),
    [state.diseases, category],
  );

  const isNone = state.diseaseCategoryFlags[category] === false;

  const toggleDisease = (disease: DiseaseType) => {
    if (selectedDiseases.has(disease)) {
      // Remove
      const toRemove = state.diseases.find(
        (d) => d.category === category && d.disease === disease,
      );
      const idsToDelete = toRemove?.id
        ? [...state.diseaseIdsToDelete, toRemove.id]
        : state.diseaseIdsToDelete;
      const remaining = state.diseases.filter(
        (d) => !(d.category === category && d.disease === disease),
      );
      const newFlags = { ...state.diseaseCategoryFlags };
      newFlags[category] = remaining.some((d) => d.category === category)
        ? true
        : null;
      updateState({
        diseaseCategoryFlags: newFlags,
        diseases: remaining,
        diseaseIdsToDelete: idsToDelete,
      });
    } else {
      // Add
      const newDisease: DiseaseDetail = { category, disease };
      const newFlags = { ...state.diseaseCategoryFlags };
      newFlags[category] = true;
      updateState({
        diseaseCategoryFlags: newFlags,
        diseases: [...state.diseases, newDisease],
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.diseases
      .filter((d) => d.category === category && d.id)
      .map((d) => d.id!);
    const newFlags = { ...state.diseaseCategoryFlags };
    newFlags[category] = false;
    updateState({
      diseaseCategoryFlags: newFlags,
      diseases: state.diseases.filter((d) => d.category !== category),
      diseaseIdsToDelete: [...state.diseaseIdsToDelete, ...idsToDelete],
    });
  };

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf(routeName);
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const isValid = isNone || state.diseaseCategoryFlags[category] === true;

  return (
    <QuestionLayout
      title={
        categoryOption
          ? t(categoryOption.questionTitleKey)
          : t("med_history.medical.disease_history.select_conditions_fallback")
      }
      subtitle={t(
        "med_history.medical.disease_history.disease_categories.subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        <Text
          style={{ color: theme.cardLabel }}
          className="mb-3 pl-1 text-sm font-medium tracking-wide"
        >
          {categoryOption ? t(categoryOption.labelKey).toUpperCase() : ""}
        </Text>
        <View style={styles.grid}>
          {diseases.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={!isNone && selectedDiseases.has(option.id)}
                onPress={() => {
                  if (isNone) {
                    // Re-select category and add this disease
                    const newFlags = { ...state.diseaseCategoryFlags };
                    newFlags[category] = true;
                    updateState({
                      diseaseCategoryFlags: newFlags,
                      diseases: [
                        ...state.diseases,
                        { category, disease: option.id },
                      ],
                    });
                  } else {
                    toggleDisease(option.id);
                  }
                }}
                labelSize="sm"
              />
            </View>
          ))}
          <View style={styles.cell}>
            <SquareOptionButton
              icon={CircleOff}
              label={t("med_history.medical.shared.none")}
              isSelected={isNone}
              onPress={handleNone}
              labelSize="sm"
            />
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
